import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth, postAuthDestination } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowRight, ChevronDown } from "lucide-react";
import { startGoogleOAuth } from "@/lib/googleAuth";

// Client first (most common signup) — intern removed: interns are hired via
// the Careers flow and provisioned as employees by admin.
const ROLE_CHOICES = [
  { value: "client", label: "Client / Researcher", desc: "Submit projects, manage mentors, track publications." },
  { value: "student", label: "Student", desc: "Learn research methods + start small projects." },
  { value: "mentor", label: "Mentor", desc: "Review projects, approve milestones, mentor researchers." },
  { value: "tutor", label: "Tutor / LMS Author", desc: "Create courses and learning content." },
  { value: "employee", label: "Employee / Staff", desc: "KhojByte staff — admin assigns the sub-role on approval." },
  { value: "partner_institution", label: "Partner Institution", desc: "Manage students, workshops, analytics for your org." },
];

export default function Register() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next");
  const { register, user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", affiliation: "", role: "client" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate(nextPath || postAuthDestination(user));
  }, [user, navigate, nextPath]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSubmitting(true);
    try {
      const u = await register(form);
      toast.success("Account created — welcome to KhojByte.");
      navigate(nextPath || postAuthDestination(u));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    startGoogleOAuth({ role: form.role || "client", next: nextPath || undefined });
  };

  const selectedRole = ROLE_CHOICES.find((r) => r.value === form.role);

  return (
    <div className="min-h-screen bg-kb-bg flex flex-col" data-testid="register-page">
      <Navbar />
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-xl">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-3">CREATE ACCOUNT</div>
          <h1 className="font-serif text-4xl text-kb-ink leading-tight">
            Get started with <em className="italic text-kb-primary">KhojByte</em>.
          </h1>
          <p className="text-sm text-kb-ink-muted mt-3">Takes under a minute. You can switch your role anytime from your profile.</p>

          <button onClick={googleLogin}
            className="mt-8 w-full border border-slate-300 bg-white text-kb-ink rounded-sm px-4 py-3 flex items-center justify-center gap-3 font-medium hover:border-kb-primary transition-colors"
            data-testid="register-google-btn">
            <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5h-1.8V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.6 5.9 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8c1.8-4.4 6-7.5 11.1-7.5 3 0 5.7 1.1 7.8 2.9l5.7-5.7C33.6 5.9 29 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 13-5l-6-5.1c-1.9 1.4-4.4 2.3-7 2.3-5.3 0-9.7-3.4-11.3-8.1l-6.6 5.1C9.4 39.6 16.1 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2.1-2.1 3.8-3.9 5l6 5.1c-.4.4 6.6-4.8 6.6-14.1 0-1.2-.1-2.3-.4-3.5z"/></svg>
            Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-light">OR</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Full name</label>
                <input required name="name" className="kb-input mt-1.5" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name-input" />
              </div>
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Email</label>
                <input type="email" name="email" required className="kb-input mt-1.5" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email-input" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Password (min 6)</label>
                <input type="password" name="password" required minLength={6} className="kb-input mt-1.5" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password-input" />
              </div>
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Affiliation (optional)</label>
                <input name="affiliation" className="kb-input mt-1.5" placeholder="e.g. Tribhuvan University" value={form.affiliation}
                  onChange={(e) => setForm({ ...form, affiliation: e.target.value })} data-testid="register-affiliation-input" />
              </div>
            </div>

            {/* Role — collapsible. Defaults to Client so the simplest user
                journey doesn't need any extra interaction here. */}
            <details className="border border-slate-200 rounded-sm bg-white" data-testid="register-role-details">
              <summary className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer list-none">
                <div className="min-w-0">
                  <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Account type</div>
                  <div className="text-sm font-medium text-kb-ink truncate">{selectedRole?.label || "Client / Researcher"}</div>
                </div>
                <ChevronDown className="w-4 h-4 text-kb-ink-muted shrink-0" />
              </summary>
              <div className="border-t border-slate-200 p-3 space-y-1.5">
                {ROLE_CHOICES.map((r) => {
                  const active = form.role === r.value;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      onClick={(e) => {
                        setForm({ ...form, role: r.value });
                        // Auto-close <details> after pick.
                        e.currentTarget.closest("details").open = false;
                      }}
                      className={`w-full text-left p-3 rounded-sm border transition-colors ${
                        active ? "border-kb-primary bg-kb-primary/5" : "border-transparent hover:border-slate-200 hover:bg-slate-50"
                      }`}
                      data-testid={`register-role-${r.value}`}
                    >
                      <div className="text-sm font-medium text-kb-ink">{r.label}</div>
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1 leading-relaxed">{r.desc}</div>
                    </button>
                  );
                })}
              </div>
            </details>

            <button type="submit" disabled={submitting} className="kb-btn-primary w-full justify-center" data-testid="register-submit-btn">
              {submitting ? "Creating account…" : (<>Create account <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          <div className="mt-8 text-sm text-kb-ink-muted text-center">
            Already have an account? <Link to="/login" className="text-kb-primary font-medium" data-testid="register-login-link">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
